import { CARD_ITEM_WIDTH } from '@/constants/dimensions';
import usePeerSupportById from '@/hooks/peersupport/usePeerSupportFromMyOrgById';
import DirectoryProfileViewPageTemplate from '@/modules/directory/DirectoryProfileViewPageTemplate';
import RequestContactModalWithTrigger from '@/modules/user/RequestContactModalWithTrigger';
import SendEmailModalWithTrigger from '@/modules/user/SendEmailModalWithTrigger';
import { useAppStore } from '@/store/useAppStore';
import colors from '@/theme/colors';
import { primary } from '@/theme/colors/palettes';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useRef } from 'react';

const PeerSupportProfileView = () => {
  const { peerSupportId } = Route.useParams();

  const { data: peerSupport, isLoading } = usePeerSupportById(peerSupportId);

  const { user } = useAppStore();
  const isViewingSelf = user?.user_id === peerSupportId;

  const phoneLinkRef = useRef<HTMLAnchorElement>(null);

  return (
    <DirectoryProfileViewPageTemplate
      isLoading={isLoading}
      name={`${peerSupport?.name}`}
      profileImage={peerSupport?.profileImage}
      role={'Peer Support Specialist'}
      description={`${peerSupport?.yearsOfAge} years old`}
    >
      {peerSupport && !isViewingSelf && (
        <HStack gap={8} marginTop={'40px'}>
          <SendEmailModalWithTrigger
            peerSupportOrMemberCare={'peer-support'}
            recipientId={peerSupport.id}
            toEmailName={peerSupport.name}
            triggerButtonWidth="170px"
            backgroundColor={colors.extra.white}
            labelColor={colors.text.mediumBlue}
            triggerButtonTransparent={false}
            hoveredBackgroundColor={colors.extra.white}
            hoveredLabelColor={colors.extra.black}
          />
          <a
            ref={phoneLinkRef}
            href={`tel:+${peerSupport.phone?.includes('+') ? peerSupport.phone.substring(1) : peerSupport.phone}`}
            style={{ display: 'none' }}
          >
            {peerSupport.phone}
          </a>

          <RequestContactModalWithTrigger
            selectedPeerSupport={peerSupport}
            triggerButtonWidth="170px"
            hoveredBackgroundColor="white"
            hoveredLabelColor={primary[600]}
          />
        </HStack>
      )}

      {isViewingSelf && (
        <Text variant={'loraSmallTitle'} color={primary[600]} marginTop={'30px'}>
          This is your own profile.
        </Text>
      )}

      {peerSupport && (
        <VStack
          backgroundColor={'white'}
          padding={'40px'}
          marginTop={'40px'}
          borderRadius={'12px'}
          alignItems={'flex-start'}
          minWidth={'500px'}
          maxWidth={CARD_ITEM_WIDTH}
          boxShadow={'0px 0px 20px 0px #00417926'}
        >
          <Text variant={'loraSmallTitle'} textAlign={'start'}>
            About Me
          </Text>
          <Text
            variant={'urbanistMediumLarge'}
            lineHeight={'22px'}
            color={'text.mediumBlue'}
            marginTop={'10px'}
            whiteSpace={'pre-wrap'}
          >
            {peerSupport.bio ?? 'No Bio'}
          </Text>
        </VStack>
      )}
    </DirectoryProfileViewPageTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_directory/_peer-support/directory/peer-support/$peerSupportId')({
  staticData: { title: 'Peer Support' },
  component: PeerSupportProfileView,
});
